import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { 
  saveCopilotSidebarState, 
  getCopilotSidebarState, 
  CopilotSidebarState 
} from '@/utils/localStorage';
import { AIAgentSidebarTab } from '@/hooks/ai-agents/types/sidebar-types';

/**
 * LocalStorageStateContext - Centralized localStorage state management
 * 
 * This context provides a single source of truth for localStorage state
 * across all components. All state changes are synchronized immediately
 * and all components re-render when state changes.
 */

interface LocalStorageStateContextType {
  // State
  activeTab: AIAgentSidebarTab;
  inputValue: string;
  currentThreadId: string | undefined;
  isModalOpen: boolean;
  
  // Operations
  setActiveTab: (tab: AIAgentSidebarTab) => void;
  setInputValue: (value: string) => void;
  setCurrentThreadId: (threadId: string | undefined) => void;
  clearInput: () => void;
  applyPrompt: (prompt: { content: string }) => void;
  setModalOpen: (isOpen: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
  getCurrentState: () => CopilotSidebarState;
}

const LocalStorageStateContext = createContext<LocalStorageStateContextType | null>(null);

export const LocalStorageStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Force re-render mechanism for all consumers
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  
  // Get current state directly from localStorage
  const getCurrentState = useCallback((): CopilotSidebarState => {
    const saved = getCopilotSidebarState();
    return saved || {
      activeTab: 'threads',
      inputValue: '',
      currentThreadId: undefined,
      isModalOpen: false
    };
  }, []);

  // Set active tab with immediate persistence and global update
  const setActiveTab = useCallback((tab: AIAgentSidebarTab) => {
    const currentState = getCurrentState();
    const newState = { ...currentState, activeTab: tab };
    saveCopilotSidebarState(newState);
    
    forceUpdate(); // Updates ALL consumers
  }, [getCurrentState]);

  // Set input value with immediate persistence and global update
  const setInputValue = useCallback((value: string) => {
    const currentState = getCurrentState();
    const newState = { ...currentState, inputValue: value };
    saveCopilotSidebarState(newState);
    
    forceUpdate(); // Updates ALL consumers
  }, [getCurrentState]);

  // Set current thread ID with immediate persistence and global update
  const setCurrentThreadId = useCallback((threadId: string | undefined) => {
    const currentState = getCurrentState();
    const newState = { ...currentState, currentThreadId: threadId };
    saveCopilotSidebarState(newState);
    
    // ✅ REACTIVE: Emit custom event for ChatManager to listen to
    window.dispatchEvent(new CustomEvent('localStorage-change', {
      detail: { key: 'currentThreadId', value: threadId }
    }));
    
    forceUpdate(); // Updates ALL consumers
  }, [getCurrentState]);

  // Clear input
  const clearInput = useCallback(() => {
    setInputValue('');
  }, [setInputValue]);

  // Apply prompt (sets input and navigates to chat)
  const applyPrompt = useCallback((prompt: { content: string }) => {
    const currentState = getCurrentState();
    const newState = { 
      ...currentState, 
      inputValue: prompt.content,
      activeTab: 'chat' as AIAgentSidebarTab
    };
    saveCopilotSidebarState(newState);
    
    forceUpdate(); // Updates ALL consumers
  }, [getCurrentState]);

  // Set modal open state with immediate persistence and global update
  const setModalOpen = useCallback((isOpen: boolean) => {
    const currentState = getCurrentState();
    const newState = { ...currentState, isModalOpen: isOpen };
    saveCopilotSidebarState(newState);
    
    forceUpdate(); // Updates ALL consumers
  }, [getCurrentState]);

  // Open modal
  const openModal = useCallback(() => {
    setModalOpen(true);
  }, [setModalOpen]);

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  // Toggle modal
  const toggleModal = useCallback(() => {
    const currentState = getCurrentState();
    setModalOpen(!currentState.isModalOpen);
  }, [getCurrentState, setModalOpen]);

  // Get current values directly from localStorage
  const currentState = getCurrentState();

  const contextValue: LocalStorageStateContextType = {
    // State (read directly from localStorage)
    activeTab: currentState.activeTab,
    inputValue: currentState.inputValue,
    currentThreadId: currentState.currentThreadId,
    isModalOpen: currentState.isModalOpen || false,
    
    // Operations
    setActiveTab,
    setInputValue,
    setCurrentThreadId,
    clearInput,
    applyPrompt,
    setModalOpen,
    openModal,
    closeModal,
    toggleModal,
    getCurrentState
  };

  return (
    <LocalStorageStateContext.Provider value={contextValue}>
      {children}
    </LocalStorageStateContext.Provider>
  );
};

/**
 * useLocalStorageState - Hook to access centralized localStorage state
 * 
 * This hook now uses the centralized context instead of creating
 * individual state instances, ensuring all components stay synchronized.
 */
export const useLocalStorageState = (): LocalStorageStateContextType => {
  const context = useContext(LocalStorageStateContext);
  if (!context) {
    throw new Error('useLocalStorageState must be used within a LocalStorageStateProvider');
  }
  return context;
}; 